import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Loader2, Wallet, HandCoins } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCurrency } from "../lib/currency";
import ProductAutocomplete from "./ProductAutocomplete";

export default function QuickSaleDialog({ open, onOpenChange, products, defaultProductId, defaultVariantId, onSaved }) {
  const { format } = useCurrency();
  const [productId, setProductId] = useState(defaultProductId || "");
  const [variantId, setVariantId] = useState(defaultVariantId || "");
  const [salePrice, setSalePrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [paymentType, setPaymentType] = useState("dinheiro");
  const [fiadoType, setFiadoType] = useState("total");
  const [amountPaid, setAmountPaid] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [saving, setSaving] = useState(false);

  const availableProducts = useMemo(
    () => (products || []).filter((p) => (p.variants || []).some((v) => v.stock > 0)),
    [products],
  );
  const product = useMemo(() => availableProducts.find((p) => p.id === productId), [availableProducts, productId]);
  const availableVariants = useMemo(
    () => (product?.variants || []).filter((v) => v.stock > 0),
    [product],
  );
  const variant = useMemo(() => availableVariants.find((v) => v.id === variantId), [availableVariants, variantId]);

  // Flat option list for autocomplete search — variant first, then product
  const searchOptions = useMemo(() => {
    const rows = [];
    for (const p of availableProducts) {
      for (const v of p.variants || []) {
        if (v.stock <= 0) continue;
        rows.push({
          productId: p.id,
          variantId: v.id,
          label: `${v.name} — ${p.name}`,
          stock: v.stock,
          image_base64: v.image_base64 || p.image_base64 || "",
        });
      }
    }
    return rows;
  }, [availableProducts]);

  useEffect(() => {
    if (open) {
      const pid = defaultProductId || availableProducts[0]?.id || "";
      setProductId(pid);
      const p = availableProducts.find((x) => x.id === pid);
      const vid = defaultVariantId || p?.variants?.find((v) => v.stock > 0)?.id || "";
      setVariantId(vid);
      const v = p?.variants?.find((x) => x.id === vid);
      setSalePrice(v ? String(v.sale_price) : "");
      setPaymentType("dinheiro");
      setFiadoType("total");
      setAmountPaid("");
      setCustomerName("");
      setQuantity(1);
    }
  }, [open, defaultProductId, defaultVariantId, availableProducts]);

  useEffect(() => {
    if (variant) setSalePrice(String(variant.sale_price));
  }, [variantId]); // eslint-disable-line

  const total = (Number(salePrice) || 0) * (Number(quantity) || 1);
  const due = paymentType === "fiado"
    ? (fiadoType === "total" ? total : Math.max(0, total - (Number(amountPaid) || 0)))
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!productId || !variantId) { toast.error("Escolhe produto e variante"); return; }
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    if (variant && qty > variant.stock) { toast.error(`Máximo em stock: ${variant.stock}`); return; }
    if (paymentType === "fiado" && !customerName.trim()) { toast.error("Nome do cliente é obrigatório"); return; }
    setSaving(true);
    try {
      await api.post("/sales", {
        product_id: productId,
        variant_id: variantId,
        sale_price: Number(salePrice) || 0,
        quantity: qty,
        payment_type: paymentType,
        fiado_type: paymentType === "fiado" ? fiadoType : null,
        amount_paid: paymentType === "fiado" && fiadoType === "parcial" ? (Number(amountPaid) || 0) : 0,
        customer_name: customerName,
      });
      toast.success("Venda registada · stock atualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registar venda");
    } finally {
      setSaving(false);
    }
  };

  const selectedProduct = availableProducts.find((x) => x.id === productId);
  const previewImage = variant?.image_base64 || selectedProduct?.image_base64 || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quick-sale-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Registar venda</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Variante · Produto</Label>
            <ProductAutocomplete
              options={searchOptions}
              value={{ productId, variantId }}
              onChange={(v) => { setProductId(v.productId); setVariantId(v.variantId); }}
              placeholder="Escrever variante ou produto…"
              testid="sale-product-autocomplete"
            />
            {variant && (
              <div className="flex items-start gap-3 mt-3 rounded-lg bg-secondary p-3">
                {previewImage ? (
                  <img src={previewImage} alt="" className="w-16 h-16 object-cover rounded-md shrink-0" data-testid="sale-preview-image" />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-white flex items-center justify-center shrink-0">
                    <span className="text-xs text-muted-foreground">sem foto</span>
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold truncate">{variant.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{selectedProduct?.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Preço sugerido: {format(variant.sale_price)} · {variant.stock} em stock
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Preço unitário (CVE)</Label>
              <Input type="number" step="0.01" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} data-testid="sale-price-input" />
            </div>
            <div className="space-y-2">
              <Label>Quantidade {variant && <span className="text-xs text-muted-foreground">(máx {variant.stock})</span>}</Label>
              <Input
                type="number"
                min="1"
                max={variant?.stock || undefined}
                value={quantity}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (variant && v > variant.stock) { setQuantity(variant.stock); return; }
                  setQuantity(v || 1);
                }}
                data-testid="sale-quantity-input"
              />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">Total: <span className="font-semibold text-foreground">{format(total)}</span></div>

          <div className="space-y-2">
            <Label>Pagamento</Label>
            <RadioGroup value={paymentType} onValueChange={setPaymentType} className="grid grid-cols-2 gap-2">
              <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${paymentType === "dinheiro" ? "border-[hsl(var(--primary))] bg-secondary" : "border-border"}`}>
                <RadioGroupItem value="dinheiro" data-testid="pay-dinheiro" />
                <Wallet className="w-4 h-4" /> <span className="text-sm font-semibold">Dinheiro</span>
              </label>
              <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${paymentType === "fiado" ? "border-[hsl(var(--accent))] bg-secondary" : "border-border"}`}>
                <RadioGroupItem value="fiado" data-testid="pay-fiado" />
                <HandCoins className="w-4 h-4" /> <span className="text-sm font-semibold">Fiado</span>
              </label>
            </RadioGroup>
          </div>

          {paymentType === "fiado" && (
            <div className="space-y-3 rounded-lg bg-secondary p-3">
              <div className="space-y-2">
                <Label>Tipo de fiado</Label>
                <RadioGroup value={fiadoType} onValueChange={setFiadoType} className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer bg-white ${fiadoType === "total" ? "border-[hsl(var(--primary))]" : "border-border"}`}>
                    <RadioGroupItem value="total" data-testid="fiado-total" /> <span className="text-sm">Total</span>
                  </label>
                  <label className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer bg-white ${fiadoType === "parcial" ? "border-[hsl(var(--primary))]" : "border-border"}`}>
                    <RadioGroupItem value="parcial" data-testid="fiado-parcial" /> <span className="text-sm">Parcial</span>
                  </label>
                </RadioGroup>
              </div>
              {fiadoType === "parcial" && (
                <div className="space-y-2">
                  <Label>Valor já pago (CVE)</Label>
                  <Input type="number" step="0.01" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} data-testid="amount-paid-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Nome do cliente *</Label>
                <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} required data-testid="customer-name-input" />
              </div>
              <div className="text-sm">Em dívida: <span className="font-semibold text-[hsl(var(--destructive))]" data-testid="amount-due-preview">{format(due)}</span></div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--accent))] hover:bg-[#C48A1E]" data-testid="confirm-sale-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmar venda
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
